import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';

import { DatePicker as MUIDatePicker } from '@mui/x-date-pickers';

// @mui/x-date-pickers' internal value-manager now calls the adapter's own isValid/getTimezone
// directly on whatever is passed as value/minDate/maxDate (no normalization step of its own),
// so a plain native Date (which every real consumer here passes, e.g. via moment(...).toDate())
// crashes AdapterMoment's isValid (`value.isValid is not a function`) — an older x-date-pickers
// version tolerated this; this one doesn't. Normalize here so consumers don't need to change.
const toMoment = value => (
  value === null || typeof value === 'undefined' || moment.isMoment(value) ? value : moment(value)
);

export const KeyboardDatePicker = ({
  DialogProps,
  KeyboardButtonProps,
  autoOk,  // eslint-disable-line no-unused-vars
  clearable,  // eslint-disable-line no-unused-vars
  error,
  format,
  fullWidth,
  helperText,
  id,
  inputProps,
  keyboardIcon,
  margin,
  maxDate,
  minDate,
  placeholder,
  value,
  ...props
}) => (
  <MUIDatePicker
    // v9 removed enableAccessibleFieldDOMStructure entirely — the segmented (per
    // month/day/year section) field is now the only option, there's no way back to a plain
    // single <input>. Matches how the sibling Atom repo handled this same removal: accept the
    // segmented field (default `PickersTextField` slot, not overridden here) and pass styling
    // through slotProps.textField instead of substituting our own TextField component.
    format={format}
    maxDate={toMoment(maxDate)}
    minDate={toMoment(minDate)}
    value={toMoment(value)}
    slots={{
      ...(keyboardIcon ? { openPickerIcon: () => keyboardIcon } : {})
    }}
    slotProps={{
      textField: {
        variant: 'standard',
        error,
        fullWidth,
        helperText,
        id,
        margin,
        placeholder,
        inputProps,
        // Matches TextField.js/Select.js's own v4-parity lineHeight override (see their
        // useStyles comments) — this field never had it, so its text rendered at v9's taller
        // default line-height (~23px) instead of the rest of the app's ~19px, standing out.
        // Applied directly on the section spans (not the field's root) for the same reason
        // TextField sets it on the input itself: resolving em against the wrong ancestor's
        // font-size distorts it.
        sx: {
          '& .MuiPickersSectionList-sectionContent': { lineHeight: '1.1876em' }
        }
      },
      openPickerButton: KeyboardButtonProps,
      dialog: DialogProps
    }}
    {...props}
  />
);

KeyboardDatePicker.propTypes = {
  DialogProps: PropTypes.object,
  KeyboardButtonProps: PropTypes.object,
  // autoOk is accepted for backward compatibility but no longer needed: v5 always closes on selection.
  autoOk: PropTypes.bool,
  // clearable is accepted for backward compatibility but still not wired to anything. This
  // version of @mui/x-date-pickers does support a real clear action (slotProps.actionBar),
  // but wiring it up is deliberately deferred to the v9 customization-review pass.
  clearable: PropTypes.bool,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  format: PropTypes.string,
  fullWidth: PropTypes.bool,
  helperText: PropTypes.node,
  id: PropTypes.string,
  inputProps: PropTypes.object,
  keyboardIcon: PropTypes.node,
  label: PropTypes.node,
  margin: PropTypes.oneOf(['none', 'dense', 'normal']),
  maxDate: PropTypes.object,
  minDate: PropTypes.object,
  onAccept: PropTypes.func,
  onBlur: PropTypes.func,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  value: PropTypes.object
};

export default KeyboardDatePicker;
